import { combineLatest, take } from "rxjs";
import { Injectable } from "@snap/ts-inject";
import { lensCoreFactory } from "../lens-core-module/loader/lensCoreFactory";
import type { LensCore } from "../lens-core-module/lensCore";
import type { SetPreloadedConfigurationInput } from "../lens-core-module/generated-types";
import { getLogger } from "../logger/logger";
import type { RemoteConfiguration } from "./remoteConfiguration";
import { remoteConfigurationFactory } from "./remoteConfiguration";
import { toLensCoreConfigValue } from "./util";

const logger = getLogger("setPreloadedConfiguration");

/**
 * Config IDs LensCore needs synchronously at startup.
 * Everything else is served on demand (see configurationProvider.ts).
 *
 * Currently empty to avoid unnecessary preloading, but may be populated later with sync config requests.
 */
const SYNC_REQUIRED_CONFIG_IDS: string[] = [];

export const setPreloadedConfiguration = Injectable(
    "setPreloadedConfiguration",
    [lensCoreFactory.token, remoteConfigurationFactory.token] as const,
    (lensCore: LensCore, remoteConfiguration: RemoteConfiguration) => {
        if (SYNC_REQUIRED_CONFIG_IDS.length === 0) {
            return;
        }

        combineLatest(SYNC_REQUIRED_CONFIG_IDS.map((id) => remoteConfiguration.get(id)))
            .pipe(take(1))
            .subscribe({
                next: (configArrays) => {
                    const inputs: SetPreloadedConfigurationInput = configArrays
                        .flat()
                        .flatMap(({ configId, value }) => {
                            if (!value) return [];
                            const convertedValue = toLensCoreConfigValue(value);
                            if (!convertedValue) return [];
                            return [{ configId, value: convertedValue }];
                        });

                    lensCore.setPreloadedConfiguration(inputs);
                },
                error: logger.error,
            });
    }
);
